/**
 * Distance over a logged track.
 *
 * Every surface that shows a trip distance — the trip card, the trip details stats, the mobile
 * detail sheet — goes through `sumTrackDistanceMeters` so they can't drift apart on what the
 * number means. Nothing here touches the database or React; the query hooks in
 * `../powersync/queries/hooks` feed it the points.
 *
 * Deliberately dependency-free: no dayjs, no drizzle. The trig has to run in JS rather than SQL
 * because PowerSync's SQLite is not guaranteed to be built with `SQLITE_ENABLE_MATH_FUNCTIONS`.
 */

/** Mean Earth radius — the figure the haversine formula below assumes. */
const EARTH_RADIUS_METERS = 6_371_000;

export const METERS_PER_NAUTICAL_MILE = 1852;
export const METERS_PER_KILOMETER = 1000;

/**
 * Legs shorter than this are treated as the boat sitting still rather than moving.
 *
 * It has to stay *below* the background tracker's `distanceFilter` (50 m in high accuracy, 200 m
 * in balanced — see the mobile `AutoLoggingProvider`), which already suppresses at-anchor GPS
 * wander for autologged trips. Raising it above that filter would start discarding real legs.
 */
const DEFAULT_MIN_LEG_METERS = 20;

/** A fix implying a speed no boat reaches is a bad fix, not movement. */
const DEFAULT_MAX_SPEED_KNOTS = 50;

/**
 * Hard ceiling on a single leg, used when the two fixes carry no usable time difference and the
 * speed check therefore can't run. 300 NM is longer than any believable gap between two logs.
 */
const DEFAULT_MAX_LEG_METERS = 300 * METERS_PER_NAUTICAL_MILE;

const KNOTS_PER_METER_PER_SECOND = 1.94384;

export type GeoPoint = {
  latitude: number;
  longitude: number;
};

export type TrackPoint = GeoPoint & {
  /** Epoch milliseconds. Absent or null disables the speed check for the legs touching it. */
  timestampMs?: number | null;
};

export type TrackDistanceOptions = {
  minLegMeters?: number;
  maxSpeedKnots?: number;
  maxLegMeters?: number;
};

export type TrackDistance = {
  meters: number;
  /** Points that anchored the walk — the first usable point plus every accepted one. */
  usedPoints: number;
  /** Points rejected as noise: a jump too big to be real, or a move too small to be movement. */
  skippedPoints: number;
  /**
   * Seconds spent on the legs that counted towards `meters`, for an average speed that ignores the
   * hours a boat spends at anchor — legs under `minLegMeters` are rejected as sitting still, so
   * their time never lands here.
   *
   * Only legs whose two fixes both carried a usable, forward-running timestamp contribute. A trip
   * logged by hand at minute precision can therefore carry distance and no moving time at all,
   * which is why `trackSpeedKnots` reports null rather than dividing by zero.
   */
  movingSeconds: number;
};

const toRadians = (degrees: number) => (degrees * Math.PI) / 180;

/**
 * `{ latitude: 0, longitude: 0 }` is what the location picker stores for "not set yet" (see the
 * end-trip form's setup and validation), so it is rejected as a sentinel here rather than
 * measured as a position in the Gulf of Guinea. A trip that genuinely starts on the null meridian
 * at the equator loses one point; every trip with an unset endpoint stops reporting a distance of
 * several thousand miles.
 */
export const isUsableGeoPoint = (point: { latitude?: number | null; longitude?: number | null }): point is GeoPoint => {
  const { latitude, longitude } = point;

  if (typeof latitude !== 'number' || typeof longitude !== 'number') {
    return false;
  }

  if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) {
    return false;
  }

  if (latitude === 0 && longitude === 0) {
    return false;
  }

  return Math.abs(latitude) <= 90 && Math.abs(longitude) <= 180;
};

/**
 * A `GeoPoint` from a loose latitude/longitude pair, or null when the pair isn't a usable position.
 * Saves every caller a cast to narrow the nullable columns the coordinates arrive in.
 */
export const toGeoPoint = (latitude?: number | null, longitude?: number | null): GeoPoint | null => {
  const point = { latitude, longitude };

  return isUsableGeoPoint(point) ? point : null;
};

/** Great-circle distance between two positions, in metres. */
export const haversineMeters = (from: GeoPoint, to: GeoPoint): number => {
  const deltaLatitude = toRadians(to.latitude - from.latitude);
  const deltaLongitude = toRadians(to.longitude - from.longitude);
  const fromLatitude = toRadians(from.latitude);
  const toLatitude = toRadians(to.latitude);

  const halfChordSquared =
    Math.sin(deltaLatitude / 2) ** 2 +
    Math.cos(fromLatitude) * Math.cos(toLatitude) * Math.sin(deltaLongitude / 2) ** 2;

  return 2 * EARTH_RADIUS_METERS * Math.asin(Math.min(1, Math.sqrt(halfChordSquared)));
};

/**
 * Total distance along `points`, which must already be ordered chronologically.
 *
 * A rejected point does not advance the anchor: the next leg is measured from the last point that
 * was trusted, so a single bad fix costs nothing rather than adding two bogus legs.
 *
 * How close the total comes to the real mileage depends entirely on how densely the trip was
 * logged. An autologged trip has a fix every 50–200 m and reads within a percent or two; a trip
 * logged by hand three times a day is a lower bound and will read well under what was sailed.
 * `usedPoints` is returned so callers can qualify the number when it is built from very few fixes,
 * and `movingSeconds` so they can turn the total into a speed — see `trackSpeedKnots`.
 */
export const sumTrackDistanceMeters = (
  points: readonly TrackPoint[],
  options: TrackDistanceOptions = {},
): TrackDistance => {
  const {
    minLegMeters = DEFAULT_MIN_LEG_METERS,
    maxSpeedKnots = DEFAULT_MAX_SPEED_KNOTS,
    maxLegMeters = DEFAULT_MAX_LEG_METERS,
  } = options;

  let meters = 0;
  let usedPoints = 0;
  let skippedPoints = 0;
  let movingSeconds = 0;
  let anchor: TrackPoint | null = null;

  for (const point of points) {
    if (!isUsableGeoPoint(point)) {
      skippedPoints += 1;
      continue;
    }

    if (!anchor) {
      anchor = point;
      usedPoints += 1;
      continue;
    }

    const legMeters = haversineMeters(anchor, point);

    if (legMeters > maxLegMeters) {
      skippedPoints += 1;
      continue;
    }

    // Manual logs are typed by hand and many carry only minute precision, so equal or inverted
    // timestamps are normal. Treating that as infinite speed would silently drop real legs —
    // fall back to the distance ceiling already applied above.
    const elapsedSeconds =
      typeof anchor.timestampMs === 'number' && typeof point.timestampMs === 'number'
        ? (point.timestampMs - anchor.timestampMs) / 1000
        : 0;

    if (elapsedSeconds > 0) {
      const speedKnots = (legMeters / elapsedSeconds) * KNOTS_PER_METER_PER_SECOND;

      if (speedKnots > maxSpeedKnots) {
        skippedPoints += 1;
        continue;
      }
    }

    if (legMeters < minLegMeters) {
      skippedPoints += 1;
      continue;
    }

    meters += legMeters;
    usedPoints += 1;

    // Guarded rather than added outright: the fallback above accepts a leg whose timestamps are
    // equal or inverted, and an inverted pair would otherwise *subtract* from the moving total and
    // report a speed higher than anything sailed. An untimed leg adds distance and no time.
    if (elapsedSeconds > 0) {
      movingSeconds += elapsedSeconds;
    }

    anchor = point;
  }

  return { meters, usedPoints, skippedPoints, movingSeconds };
};

export type TripEndpoints = {
  origin_latitude?: number | null;
  origin_longitude?: number | null;
  destination_latitude?: number | null;
  destination_longitude?: number | null;
  started_at?: string | null;
  finished_at?: string | null;
  closed_at?: string | null;
};

export type TripTrackLogPoint = {
  latitude?: number | null;
  longitude?: number | null;
  datetime?: string | null;
};

/**
 * The points a trip's distance is measured over: its logged positions, bracketed by the trip's own
 * origin and destination when those are set.
 *
 * The endpoints are included for the same reason the map draws start and finish markers from them
 * (`useTripPath`) — the first and last legs of a trip are usually the stretch between the marina
 * and the first fix. Including them keeps the number consistent with the line the user is looking
 * at. When an endpoint sits on top of the adjacent log the leg falls under `minLegMeters` and
 * contributes nothing, so there is no double counting.
 */
export const buildTripTrackPoints = (
  trip: TripEndpoints | null | undefined,
  logPoints: readonly TripTrackLogPoint[],
): TrackPoint[] => {
  const points: TrackPoint[] = [];

  const origin = toGeoPoint(trip?.origin_latitude, trip?.origin_longitude);

  if (origin) {
    points.push({ ...origin, timestampMs: parseLogTimestampMs(trip?.started_at) });
  }

  for (const logPoint of logPoints) {
    const position = toGeoPoint(logPoint.latitude, logPoint.longitude);

    if (position) {
      points.push({ ...position, timestampMs: parseLogTimestampMs(logPoint.datetime) });
    }
  }

  const destination = toGeoPoint(trip?.destination_latitude, trip?.destination_longitude);

  if (destination) {
    points.push({ ...destination, timestampMs: parseLogTimestampMs(trip?.finished_at ?? trip?.closed_at) });
  }

  return points;
};

/**
 * Thins a path to at most `maxPoints`, keeping the first and last and spreading the rest evenly.
 *
 * For the static map covers, whose URL has a length cap. Truncating the track instead — taking the
 * first N points — draws only the opening leg of a long trip.
 */
export const downsamplePath = <TPoint>(points: readonly TPoint[], maxPoints: number): TPoint[] => {
  if (maxPoints < 2 || points.length <= maxPoints) {
    return [...points];
  }

  const step = (points.length - 1) / (maxPoints - 1);

  return Array.from({ length: maxPoints }, (_, index) => points[Math.round(index * step)]);
};

export const metersToNauticalMiles = (meters: number) => meters / METERS_PER_NAUTICAL_MILE;

export const metersToKilometers = (meters: number) => meters / METERS_PER_KILOMETER;

/** A knot is one nautical mile an hour, so this is the same ratio `metersToKilometers` applies. */
export const knotsToKilometersPerHour = (knots: number) => (knots * METERS_PER_NAUTICAL_MILE) / METERS_PER_KILOMETER;

/**
 * Average speed over a measured track, in knots — or null when the track carries no moving time to
 * divide by.
 *
 * This is speed *while moving*, not distance over the trip's wall-clock duration: `movingSeconds`
 * counts only the legs that survived the noise filters, so a week at anchor in the middle of a
 * passage does not drag the number towards zero. Callers wanting the passage average should divide
 * `meters` by the trip's own `started_at`/`finished_at` span instead.
 *
 * Knots whatever the user's unit setting; callers convert for display (`knotsToKilometersPerHour`).
 *
 * Uses the same conversion constant as the per-leg outlier check above, so a track whose every leg
 * passed a 50-knot ceiling cannot average out above it.
 */
export const trackSpeedKnots = (track: Pick<TrackDistance, 'meters' | 'movingSeconds'>): number | null => {
  const { meters, movingSeconds } = track;

  if (movingSeconds <= 0) {
    return null;
  }

  return (meters / movingSeconds) * KNOTS_PER_METER_PER_SECOND;
};

/**
 * Epoch milliseconds for a stored log `datetime`, or null when it can't be read.
 *
 * Log datetimes are written in a handful of UTC shapes — `YYYY-MM-DD HH:mm` from some form paths,
 * `HH:mm:ss` from the mobile autologger, `HH:mm:ss.SSS` from `momentToDateTimeWithMS` — and are
 * parsed here rather than with `new Date(...)`, which does not reliably accept the space-separated
 * forms on Hermes or Safari.
 */
export const parseLogTimestampMs = (value?: string | null): number | null => {
  if (!value) {
    return null;
  }

  const match = /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})(?::(\d{2}))?(?:\.(\d{1,3}))?/.exec(value);

  if (!match) {
    return null;
  }

  const [, year, month, day, hours, minutes, seconds, milliseconds] = match;

  const timestamp = Date.UTC(
    Number(year),
    Number(month) - 1,
    Number(day),
    Number(hours),
    Number(minutes),
    Number(seconds ?? 0),
    Number((milliseconds ?? '').padEnd(3, '0') || 0),
  );

  return Number.isNaN(timestamp) ? null : timestamp;
};
